/*
 * Copyright 2017-2017 Amazon.com, Inc. or its affiliates. All Rights Reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License"). You may not use this file except in compliance with
 * the License. A copy of the License is located at
 *
 *   http://aws.amazon.com/apache2.0/
 *
 * or in the "license" file accompanying this file. This file is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR
 * CONDITIONS OF ANY KIND, either express or implied. See the License for the specific language governing permissions
 * and limitations under the License.
 */

import * as React from 'react';

import { ConsoleLogger as Logger, I18n } from '@aws-amplify/core';
import Auth from '@aws-amplify/auth';

import { AuthPiece } from 'aws-amplify-react';
import client from 'helpers/client';
import paths from 'redux/paths';

import {
  Button,
  FormField,
  FormSection,
  Input,
  InputLabel,
  Link,
  SectionBody,
  SectionFooter,
  SectionFooterPrimaryContent,
  SectionFooterSecondaryContent,
  SectionHeader
} from 'aws-amplify-react/lib/Amplify-UI/Amplify-UI-Components-React';
import AmplifyTheme from 'aws-amplify-react/lib/Amplify-UI/Amplify-UI-Theme';

const logger = new Logger('ForgotPassword');

export default class ForgotPassword extends AuthPiece {
  constructor(props) {
    super(props);

    this.send = this.send.bind(this);
    this.submit = this.submit.bind(this);

    this._validAuthStates = ['forgotPassword'];
    this.state = {
      delivery: null,
      errorMessage: '',
      showVerifyButton: false
    };
  }

  async send() {
    const { authData = {} } = this.props;
    let username = this.inputs.username || authData.username;
    if (username) username = username.replace(/\s/g, '').toLowerCase();

    if (!Auth || typeof Auth.forgotPassword !== 'function') {
      throw new Error('No Auth module found, please ensure @aws-amplify/auth is imported');
    }
    try {
      let dataAuth = await Auth.forgotPassword(username)
      this.setState({ delivery: dataAuth.CodeDeliveryDetails });
    } catch(err) {
      let errorCode = err.code
      let errorMsg = err.message
      console.log('err', errorCode, errorMsg)
      if (errorCode == "NotAuthorizedException" || errorCode == "InvalidParameterException") {
        client.sendRequest({
          path: paths.CHECK_AWS_USERNAME,
          method: 'POST',
          params: { username }
        }).then((data) => {
           console.log("data", data)
           let statusCurrent = data.statusUser
           if (statusCurrent == 'FORCE_CHANGE_PASSWORD') {
             this.error(err)
           } else if (statusCurrent == 'UNCONFIRMED') {
             logger.debug('the user is not confirmed');
             this.changeState('confirmSignUp', username);
           } else if (statusCurrent == 'CONFIRMED') {
             Auth.forgotPassword(username).then((data) => {
               logger.debug(data);
               this.setState({ delivery: data.CodeDeliveryDetails });
             })
             .catch((err) => {
               this.error(err)
             });
           }
         })

      } else {
        this.error(err)
      }
    }

  }

  submit() {
    const { authData = {} } = this.props;
    let { code = '', password } = this.inputs;
    code = code.replace(/\s/g, '');
    const username = this.inputs.username || authData.username;

    if (!Auth || typeof Auth.forgotPasswordSubmit !== 'function') {
      throw new Error('No Auth module found, please ensure @aws-amplify/auth is imported');
    }
    Auth.forgotPasswordSubmit(username, code, password)
      .then((data) => {
        logger.debug(data);
        this.changeState('signIn');
        this.setState({ delivery: null });
      })
      .catch((err) => this.error(err));
  }

  sendView() {
    const theme = this.props.theme || AmplifyTheme;
    return (
      <div>
        <FormField theme={theme}>
          <InputLabel theme={theme}>{I18n.get('Email')} *</InputLabel>
          <Input
            autoFocus
            placeholder={I18n.get('Enter your email')}
            theme={theme}
            key="username"
            name="username"
            onChange={this.handleInputChange}
          />
        </FormField>
      </div>
    );
  }

  submitView() {
    const theme = this.props.theme || AmplifyTheme;
    return (
      <div>
        <Input
          placeholder={I18n.get('Code')}
          theme={theme}
          key="code"
          name="code"
          autoComplete="off"
          onChange={this.handleInputChange}
        />
        <Input
          placeholder={I18n.get('New Password')}
          theme={theme}
          type="password"
          key="password"
          name="password"
          onChange={this.handleInputChange}
        />
      </div>
    );
  }

  showComponent(theme) {
    const { hide, authData = {} } = this.props;
    if (hide && hide.includes(ForgotPassword)) {
      return null;
    }

    return (
      <FormSection theme={theme}>
        <SectionHeader theme={theme}>{I18n.get('Reset your password')}</SectionHeader>
        <SectionBody theme={theme}>
          {this.state.delivery || authData.username ? this.submitView() : this.sendView()}
        </SectionBody>
        <SectionFooter theme={theme}>
          <SectionFooterPrimaryContent theme={theme}>
            {this.state.delivery || authData.username ? (
              <Button theme={theme} onClick={this.submit}>
                {I18n.get('Submit')}
              </Button>
            ) : (
              <Button theme={theme} onClick={this.send}>
                {I18n.get('Send Code')}
              </Button>
            )}
          </SectionFooterPrimaryContent>
          <SectionFooterSecondaryContent theme={theme}>
            {this.state.delivery || authData.username ? (
              <Link theme={theme} onClick={this.send}>
                {I18n.get('Resend Code')}
              </Link>
            ) : (
              <Link color="primary" theme={theme} onClick={() => this.changeState('signIn')}>
                {I18n.get('Back to Sign In')}
              </Link>
            )}
          </SectionFooterSecondaryContent>
        </SectionFooter>
      </FormSection>
    );
  }
}
